import { useEffect, useState } from 'react';

export enum SWPhase {
  LOADING = 'loading',
  CACHING = 'caching',
  UPDATING = 'updating',
  READY = 'ready',
  UNAVAILABLE = 'unavailable',
}

export function useSWStatus() {
  const [phase, setPhase] = useState<SWPhase>(() => {
    if (!('serviceWorker' in navigator)) return SWPhase.UNAVAILABLE;
    // If a SW already controls this page, we're offline-ready right now
    if (navigator.serviceWorker.controller) return SWPhase.READY;
    return SWPhase.LOADING;
  });

  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;
    // Already ready — nothing to watch
    if (navigator.serviceWorker.controller) return;

    let settled = false;
    let timeout: ReturnType<typeof setTimeout> | null = null;

    const markReady = () => {
      if (settled) return;
      settled = true;
      if (timeout) clearTimeout(timeout);
      setPhase(SWPhase.READY);
    };

    const check = async () => {
      const reg = await navigator.serviceWorker
        .getRegistration()
        .catch(() => null);

      if (!reg) {
        // SW not registered yet — wait
        navigator.serviceWorker.ready
          .then(() => {
            // ready resolves when active SW exists; controller may still be null
            // until page reload, but the SW is active
            if (navigator.serviceWorker.controller) {
              markReady();
            } else {
              // SW active but not yet controlling (first install — page will reload
              // via main.tsx controllerchange handler). Show caching until then.
              setPhase(SWPhase.CACHING);
            }
          })
          .catch(() => setPhase(SWPhase.UNAVAILABLE));
        return;
      }

      if (reg.active && !reg.installing) {
        // Active SW but no controller = page was loaded before SW was installed.
        // main.tsx will reload on controllerchange. Show caching until reload.
        setPhase(SWPhase.CACHING);
      } else if (reg.installing) {
        setPhase(SWPhase.CACHING);
        reg.installing.addEventListener('statechange', function () {
          if (this.state === 'activated') markReady();
        });
      }
    };

    // controllerchange fires when SW takes control of this page.
    // Note: main.tsx also listens and reloads — that's fine, markReady is a no-op
    // after the component unmounts on reload.
    navigator.serviceWorker.addEventListener('controllerchange', markReady);

    check();

    // Hard failsafe: never stay in LOADING/CACHING forever
    timeout = setTimeout(() => {
      if (!settled) markReady();
    }, 30_000);

    return () => {
      settled = true;
      if (timeout) clearTimeout(timeout);
      navigator.serviceWorker.removeEventListener(
        'controllerchange',
        markReady
      );
    };
  }, []);

  // Separate effect: detect SW updates on an already-active controller.
  // Fires when the browser finds a new SW version and starts installing it.
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;
    let cancelled = false;
    let removeListener: (() => void) | null = null;

    navigator.serviceWorker.ready.then(reg => {
      if (cancelled) return;
      const onUpdateFound = () => {
        if (cancelled) return;
        // Only UPDATING if we already had a controller — otherwise it's first install
        if (navigator.serviceWorker.controller) {
          setPhase(SWPhase.UPDATING);
        }
      };
      reg.addEventListener('updatefound', onUpdateFound);
      removeListener = () =>
        reg.removeEventListener('updatefound', onUpdateFound);
    });

    return () => {
      cancelled = true;
      removeListener?.();
    };
  }, []);

  return { phase };
}
